import { createContext, useContext, createEffect } from "solid-js";
import { saveUserData, getUserData, saveConventionsData, getConventionsData } from "./offlineDB";
import { useConnection } from "./ConnectionManager";

// Create context for offline data management
const OfflineDataContext = createContext();

export function OfflineDataManagerProvider(props: any) {
  const [connectionStore] = useConnection();
  
  // Create effect to watch for offline state changes
  createEffect(() => {
    if (connectionStore.isOffline) {
      console.log("Application went offline, will use cached data");
    } else {
      console.log("Application is online, will use live data");
    }
  });

  // Offline data manager functions
  const offlineDataManager = {
    // User data functions
    saveUserData: async (userData: any) => {
      if (!userData?.username) {
        console.warn("Cannot save empty user data to cache");
        return false;
      }
      
      console.log('Saving user data to IndexedDB for offline use:', userData);
      try {
        return await saveUserData(userData);
      } catch (error) {
        console.error("Error saving user data:", error);
        return false;
      }
    },
    
    loadUserData: async () => {
      console.log('Loading user data from cache...');
      try {
        const data = await getUserData();
        if (data) {
          console.log('Loaded cached user data:', data);
          return data;
        } else {
          console.log('No cached user data found.');
          return null;
        }
      } catch (error) {
        console.error("Error loading user data:", error);
        return null;
      }
    },
    
    // Conventions data functions
    saveConventionsData: async (data: any) => {
      if (!data || (Array.isArray(data) && data.length === 0)) {
        console.warn("Cannot save empty conventions data to cache");
        return false;
      }
      
      console.log('Saving conventions data to IndexedDB for offline use');
      try {
        return await saveConventionsData(data);
      } catch (error) {
        console.error("Error saving conventions data:", error);
        return false;
      }
    },
    
    loadConventionsData: async () => {
      console.log('Loading conventions data from cache...');
      try {
        const result = await getConventionsData();
        if (result && result.data) {
          console.log('Loaded cached conventions data');
          return {
            conventions: result.data,
            lastUpdated: result.timestamp
          };
        } else {
          console.log('No cached conventions data found.');
          return null;
        }
      } catch (error) {
        console.error("Error loading conventions data:", error);
        return null;
      }
    },
    
    // Helper functions
    isOffline: () => connectionStore.isOffline
  };

  return (
    <OfflineDataContext.Provider value={offlineDataManager}>
      {props.children}
    </OfflineDataContext.Provider>
  );
}

// Hook for components to access offline data manager
export function useOfflineData() {
  return useContext(OfflineDataContext);
}